import { memo, useCallback, useMemo } from "react";
import type { Moment } from "moment";
import { IAssignment } from "@miplatform/types";
import { subtractFromBusinessDate } from "@miplatform/utils";
import { AssignmentCard } from "./AssignmentCard";
import { AssignmentUserSortBy } from "./AssignmentsCalendar.constants";
import type { IAssignmentCalendarUser } from "./AssignmentsCalendar.types";
import { AssignmentsColumn } from "./AssignmentsColumn";

interface AssignmentsUsersRendererProps {
  date: Moment;
  users: IAssignmentCalendarUser[];
  isCurrentOnly?: boolean;
  isPublishedOnly?: boolean;
  sortBy?: AssignmentUserSortBy;
  selectedUser: IAssignmentCalendarUser | null;
  minHeight?: number;
  onSelectedUserChange?: (user: IAssignmentCalendarUser | null) => void;
  onUnassignAssignment: (assignmentId: string) => void;
  onPublishAssignment: (assignmentId: string) => void;
}

export const AssignmentsUsersRenderer = memo(
  ({
    date,
    users,
    isCurrentOnly,
    isPublishedOnly,
    selectedUser,
    minHeight,
    onSelectedUserChange,
    onUnassignAssignment,
    onPublishAssignment,
  }: AssignmentsUsersRendererProps) => {
    const userDates = useMemo(
      () => [date, subtractFromBusinessDate(date, 1)],
      [date]
    );

    const getUserAssignments = useCallback(
      (user: IAssignmentCalendarUser) => {
        if (isPublishedOnly) {
          return user.currentPublishedAssignments;
        }

        if (isCurrentOnly) {
          return user.currentAssignments;
        }

        return [
          ...user.previousAssignmentDateAssignments,
          ...user.assignmentDateAssignments,
        ];
      },
      [isCurrentOnly, isPublishedOnly]
    );

    const getUserHeaderClickHandler = useCallback(
      (user: IAssignmentCalendarUser) =>
        onSelectedUserChange ? () => onSelectedUserChange(user) : undefined,
      [onSelectedUserChange]
    );

    const handleResetSelectedUser = useCallback(
      () => onSelectedUserChange?.(null),
      [onSelectedUserChange]
    );

    const renderAssignmentCards = (assignments: IAssignment[]) =>
      assignments.map((assignment) => (
        <AssignmentCard
          key={assignment.id}
          assignment={assignment}
          isAssignDisabled
          onUnassign={onUnassignAssignment}
          onPublish={onPublishAssignment}
        />
      ));

    const renderUsers = () =>
      users.map((user) => {
        const assignments = getUserAssignments(user);
        const publishedAssignmentsCount = (
          isPublishedOnly
            ? user.currentPublishedAssignments
            : user.currentAssignments.filter(({ publishedOn }) =>
                Boolean(publishedOn)
              )
        ).length;
        const additionalCount = isPublishedOnly
          ? 0
          : assignments.filter(
              ({ assigneeId, publishedOn }) => assigneeId && !publishedOn
            ).length;

        return (
          <AssignmentsColumn
            key={user.id}
            isSelected={selectedUser?.id === user.id}
            isDisabled={Boolean(selectedUser && selectedUser?.id !== user.id)}
            title={user.displayName}
            count={publishedAssignmentsCount}
            additionalCount={additionalCount}
            minHeight={minHeight}
            sections={
              !(isCurrentOnly || isPublishedOnly)
                ? userDates.map((date, index) => {
                    const assignments =
                      index === 0
                        ? user.assignmentDateAssignments
                        : user.previousAssignmentDateAssignments;

                    return {
                      date,
                      count: assignments.length,
                      children:
                        assignments.length > 0
                          ? renderAssignmentCards(assignments)
                          : null,
                    };
                  })
                : undefined
            }
            onClick={
              selectedUser && selectedUser?.id !== user.id
                ? handleResetSelectedUser
                : undefined
            }
            onHeaderClick={
              selectedUser?.id === user.id
                ? handleResetSelectedUser
                : getUserHeaderClickHandler(user)
            }
          >
            {(isCurrentOnly || isPublishedOnly) && assignments.length > 0
              ? renderAssignmentCards(assignments)
              : null}
          </AssignmentsColumn>
        );
      });

    return renderUsers();
  }
);
